import { useCallback, useEffect, useMemo } from "react";

import { isLovablePreview } from "@/lib/utils/environment";
import { useWorkspaceId } from "@/lib/hooks/use-workspace-id";
import { useWorkspaceLocalStorage } from "@/hooks/useWorkspaceLocalStorage";
import {
  useGetClipsQuery,
  useCreateClipMutation,
  useUpdateClipMutation,
  useDeleteClipMutation,
  type ClipFieldsFragment,
} from "@/lib/graphql/types-and-hooks";

export const CLIP_STATUS = {
  PENDING: "PENDING",
  READY: "READY",
  FAILED: "FAILED",
} as const;

export interface ClipTranscriptSegment {
  speaker?: string | null;
  text?: string | null;
  start?: number | null;
  end?: number | null;
}

export interface Clip {
  id: string;
  name: string;
  startSec: number;
  endSec: number;
  status: string; // PENDING | READY | FAILED
  statusError?: string | null;
  clipVideoUrl?: string | null;
  shareSlug?: string | null;
  transcript: ClipTranscriptSegment[];
}

export interface CreateClipInput {
  startSec: number;
  endSec: number;
  name: string;
}

export interface UseClipsResult {
  clips: Clip[];
  loading: boolean;
  createClip: (input: CreateClipInput) => Promise<Clip | null>;
  renameClip: (id: string, name: string) => Promise<void>;
  deleteClip: (id: string) => Promise<void>;
}

const CLIPS_STORAGE_PREFIX = "clips_v1";

/** Map a backend clip row into the UI Clip shape. Exported for unit tests. */
export function toClip(row: ClipFieldsFragment): Clip {
  return {
    id: row.id ?? "",
    name: row.name ?? "Clip",
    startSec: row.startSec ?? 0,
    endSec: row.endSec ?? 0,
    status: row.status ?? CLIP_STATUS.READY,
    statusError: row.statusError ?? null,
    clipVideoUrl: row.clipVideoUrl ?? null,
    shareSlug: row.shareSlug ?? null,
    transcript: (row.transcript ?? [])
      .filter((s): s is NonNullable<typeof s> => !!s)
      .map((s) => ({ speaker: s.speaker, text: s.text, start: s.start, end: s.end })),
  };
}

/**
 * Dual-mode clips hook (Pattern 00). Production persists clips via the Clip
 * GraphQL API and polls PENDING clips until the extraction workflow flips them
 * to READY. Preview mode has no backend, so clips live in workspace-scoped
 * localStorage (keyed per engagement) and never extract a real video.
 */
export function useClips(engagementId?: string | null): UseClipsResult {
  const preview = isLovablePreview();
  const workspaceIdRaw = useWorkspaceId();
  const workspaceId = workspaceIdRaw ? Number(workspaceIdRaw) : null;

  // Preview: per-engagement local persistence.
  const [previewClips, setPreviewClips] = useWorkspaceLocalStorage<Clip[]>(
    `${CLIPS_STORAGE_PREFIX}:${engagementId ?? "none"}`,
    [],
  );

  const skip = preview || workspaceId == null || !engagementId;

  const { data, loading, startPolling, stopPolling } = useGetClipsQuery({
    skip,
    variables: skip
      ? (undefined as never)
      : { workspaceId: workspaceId as number, engagementId: engagementId as string },
    fetchPolicy: "cache-and-network",
  });

  const [createMutation] = useCreateClipMutation();
  const [updateMutation] = useUpdateClipMutation();
  const [deleteMutation] = useDeleteClipMutation();

  const backendClips = useMemo<Clip[]>(
    () => (data?.clips ?? []).filter((c): c is ClipFieldsFragment => !!c).map(toClip),
    [data?.clips],
  );

  // Poll while any clip is still being extracted, then stop.
  const hasPending = backendClips.some((c) => c.status === CLIP_STATUS.PENDING);
  useEffect(() => {
    if (skip) return;
    if (hasPending) startPolling(3000);
    else stopPolling();
    return () => stopPolling();
  }, [skip, hasPending, startPolling, stopPolling]);

  const createClip = useCallback<UseClipsResult["createClip"]>(
    async ({ startSec, endSec, name }) => {
      if (preview) {
        const created: Clip = {
          id: `clip-${Date.now()}`,
          name,
          startSec,
          endSec,
          // No backend to extract a video in preview — surface as READY so the
          // UI is usable, but there is no clipVideoUrl / shareable link.
          status: CLIP_STATUS.READY,
          clipVideoUrl: null,
          shareSlug: null,
          transcript: [],
        };
        setPreviewClips((prev) => [created, ...prev]);
        return created;
      }
      if (workspaceId == null || !engagementId) throw new Error("workspace/engagement required");
      const res = await createMutation({
        variables: { workspaceId, engagementId, startSec, endSec, name },
        refetchQueries: ["getClips"],
      });
      const r = res.data?.createClip;
      return r ? toClip(r) : null;
    },
    [preview, setPreviewClips, createMutation, workspaceId, engagementId],
  );

  const renameClip = useCallback<UseClipsResult["renameClip"]>(
    async (id, name) => {
      if (preview) {
        setPreviewClips((prev) => prev.map((c) => (c.id === id ? { ...c, name } : c)));
        return;
      }
      if (workspaceId == null) throw new Error("workspace required");
      await updateMutation({ variables: { workspaceId, id, name }, refetchQueries: ["getClips"] });
    },
    [preview, setPreviewClips, updateMutation, workspaceId],
  );

  const deleteClip = useCallback<UseClipsResult["deleteClip"]>(
    async (id) => {
      if (preview) {
        setPreviewClips((prev) => prev.filter((c) => c.id !== id));
        return;
      }
      if (workspaceId == null) throw new Error("workspace required");
      await deleteMutation({ variables: { workspaceId, id }, refetchQueries: ["getClips"] });
    },
    [preview, setPreviewClips, deleteMutation, workspaceId],
  );

  return {
    clips: preview ? previewClips : backendClips,
    loading: preview ? false : loading,
    createClip,
    renameClip,
    deleteClip,
  };
}
